import { apiClient } from './client';
import type { components } from './schema.gen';

export type DLQItem = components['schemas']['DLQView'];
export type AuditItem = components['schemas']['AuditView'];

export type DLQFilters = {
  source?: string;
  code?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
};

export async function listDLQ(filters: DLQFilters = {}): Promise<{ items: DLQItem[]; total: number }> {
  const { data, error } = await apiClient.GET('/v1/admin/dlq', {
    params: {
      query: {
        source: filters.source ?? '',
        code: filters.code ?? '',
        from: filters.from ?? '',
        to: filters.to ?? '',
        limit: filters.limit ?? 50,
        offset: filters.offset ?? 0,
      },
    },
  });
  if (error || !data) return { items: [], total: 0 };
  return { items: (data.items ?? []) as DLQItem[], total: data.total ?? 0 };
}

export type AuditFilters = {
  action?: string;
  targetKind?: string;
  targetId?: string;
  actor?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
};

export async function listAudit(filters: AuditFilters = {}): Promise<{ items: AuditItem[]; total: number }> {
  const { data, error } = await apiClient.GET('/v1/admin/audit', {
    params: {
      query: {
        action: filters.action ?? '',
        targetKind: filters.targetKind ?? '',
        targetId: filters.targetId ?? '',
        actor: filters.actor ?? '',
        from: filters.from ?? '',
        to: filters.to ?? '',
        limit: filters.limit ?? 50,
        offset: filters.offset ?? 0,
      },
    },
  });
  if (error || !data) return { items: [], total: 0 };
  return { items: (data.items ?? []) as AuditItem[], total: data.total ?? 0 };
}

export async function resolveConflict(
  phone: string,
  input: {
    action: 'MERGE' | 'KEEP_SEPARATE';
    reason: string;
    primaryPhone?: string;
    involvedPhones?: string[];
  },
): Promise<{ ok: true; eventId: string } | { ok: false; message: string }> {
  const { data, error, response } = await apiClient.POST(
    '/v1/entrepreneurs/{phone}/resolve-conflict',
    {
      params: { path: { phone } },
      body: input,
    },
  );
  if (error || !data) {
    const e = error as { detail?: string } | undefined;
    return { ok: false, message: e?.detail ?? `HTTP ${response.status}` };
  }
  return { ok: true, eventId: data.eventId };
}

/**
 * Phrase exacte attendue par l'API. Longue et non traduite à dessein : elle
 * doit être recopiée, ce qu'aucun enchaînement de clics ne produit par accident.
 */
export const PURGE_CONFIRMATION = 'EFFACER DEFINITIVEMENT LES DONNEES METIER';

export type PurgeResult = {
  entrepreneurs: number;
  events: number;
  imports: number;
  dlq: number;
  embeddings: number;
  actorsPurged: boolean;
  message: string;
};

/**
 * Remise à zéro des données métier — IRRÉVERSIBLE.
 *
 * Efface dossiers, journal d'événements, embeddings, rejets et lots d'import.
 * Conserve dictionnaire, utilisateurs, clés d'API, partenaires, mappings et
 * tous les journaux (audit compris). L'API refuse si l'environnement n'a pas
 * été ouvert par MD_DANGER__ALLOW_PURGE, si l'appelant n'est pas ADMIN, ou si
 * la phrase ne correspond pas exactement.
 */
export async function purgeBusinessData(
  confirmation: string,
  includeActors = false,
): Promise<{ ok: true; result: PurgeResult } | { ok: false; message: string }> {
  const { data, error, response } = await apiClient.POST('/v1/admin/purge', {
    body: { confirmation, includeActors },
  });
  if (error || !data) {
    const e = error as { detail?: string } | undefined;
    return { ok: false, message: e?.detail ?? `HTTP ${response.status}` };
  }
  return { ok: true, result: data as PurgeResult };
}

// --- Sauvegardes : cycle de vie complet côté serveur ------------------------

export type Backup = {
  id: number;
  filename: string;
  origin: 'GENERATED' | 'UPLOADED';
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  sizeBytes?: number;
  counts?: Record<string, number>;
  errorMessage?: string;
  createdAt: string;
  completedAt?: string;
  actorLabel?: string;
  restoredAt?: string;
  restoredEvents?: number;
  restoreStatus?: 'RUNNING' | 'COMPLETED' | 'FAILED';
  restoreError?: string;
  fileMissing: boolean;
};

export type BackupList = { items: Backup[]; dir: string; persistent: boolean };

export async function listBackups(): Promise<BackupList> {
  const { data, error } = await apiClient.GET('/v1/admin/backups', {});
  if (error || !data) return { items: [], dir: '', persistent: false };
  return data as BackupList;
}

/** Lance la production d'une sauvegarde. Asynchrone : suivre via listBackups. */
export async function createBackup(): Promise<{ ok: boolean; message?: string }> {
  const { error, response } = await apiClient.POST('/v1/admin/backups', {});
  if (error) {
    const e = error as { detail?: string } | undefined;
    return { ok: false, message: e?.detail ?? `HTTP ${response.status}` };
  }
  return { ok: true };
}

export async function restoreBackup(id: number): Promise<{ ok: boolean; message?: string }> {
  const { error, response } = await apiClient.POST('/v1/admin/backups/{id}/restore', {
    params: { path: { id } },
  });
  if (error) {
    const e = error as { detail?: string } | undefined;
    return { ok: false, message: e?.detail ?? `HTTP ${response.status}` };
  }
  return { ok: true };
}

export async function deleteBackup(id: number): Promise<boolean> {
  const { error } = await apiClient.DELETE('/v1/admin/backups/{id}', {
    params: { path: { id } },
  });
  return !error;
}

/**
 * Téléchargement et téléversement passent par fetch : ce sont des flux binaires
 * (gzip, multipart), hors du champ du client typé qui ne parle que JSON.
 */
export function backupDownloadUrl(id: number): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  return `${base}/v1/admin/backups/${id}/download`;
}

export async function downloadBackupFile(
  id: number,
  filename: string,
  accessToken: string,
): Promise<{ ok: boolean; message?: string }> {
  let res: Response;
  try {
    res = await fetch(backupDownloadUrl(id), {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'réseau indisponible' };
  }
  if (!res.ok) return { ok: false, message: `HTTP ${res.status}` };

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return { ok: true };
}

export async function uploadBackupFile(
  file: File,
  accessToken: string,
): Promise<{ ok: boolean; message?: string }> {
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const form = new FormData();
  form.append('file', file);
  let res: Response;
  try {
    res = await fetch(`${base}/v1/admin/backups/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: form,
    });
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'réseau indisponible' };
  }
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    return { ok: false, message: txt.trim() || `HTTP ${res.status}` };
  }
  return { ok: true };
}
